import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useCart } from "../lib/cart";

const MASK_ICON = "https://customer-assets.emergentagent.com/job_classic-demolition/artifacts/8lulpm32_Gemini_Generated_Image_9hnmyf9hnmyf9hnm.png";

const navLinks = [
  { path: "/", label: "HOME" },
  { path: "/about", label: "ABOUT" },
  { path: "/media", label: "MEDIA" },
  { path: "/youtube", label: "YOUTUBE" },
  { path: "/events", label: "EVENTS" },
  { path: "/shop", label: "SHOP" },
];

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { count, openCart } = useCart();

  return (
    <nav 
      className="sticky top-0 z-50 backdrop-blur-md bg-black/90 border-b border-white/10"
      data-testid="main-navigation"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 group"
            data-testid="nav-logo"
          >
            <img 
              src={MASK_ICON} 
              alt="Demolition Mask" 
              className="w-10 h-10 object-contain transition-transform group-hover:scale-110" 
            />
            <span className="font-metal text-2xl md:text-3xl tracking-tight text-white uppercase">
              AX AND SMASH
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={`font-teko text-xl tracking-widest uppercase transition-colors
                  ${location.pathname === link.path 
                    ? "text-demolition-red" 
                    : "text-white/80 hover:text-demolition-red"
                  }`}
              >
                {link.label}
              </Link>
            ))}
            <CartButton count={count} onClick={openCart} />
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center gap-1">
            <CartButton count={count} onClick={openCart} />
            <button
              className="text-white p-2"
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div 
            className="md:hidden pb-6 border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-4 pt-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase()}`}
                  className={`font-teko text-2xl tracking-widest uppercase transition-colors
                    ${location.pathname === link.path 
                      ? "text-demolition-red" 
                      : "text-white/80 hover:text-demolition-red"
                    }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function CartButton({ count, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative p-2 text-white/80 hover:text-demolition-red transition-colors"
      aria-label={`Open cart${count > 0 ? ` (${count} items)` : ""}`}
      data-testid="nav-cart-button"
    >
      <ShoppingBag className="w-6 h-6" />
      {count > 0 && (
        <span
          className="absolute -top-0.5 -right-0.5 min-w-[1.1rem] h-[1.1rem] px-1 flex items-center justify-center
            bg-demolition-red text-white font-russo text-[10px] leading-none tracking-tight"
          data-testid="nav-cart-count"
        >
          {count > 99 ? "99+" : count}
        </span>
      )}
    </button>
  );
}
